import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtINR, BUCKET_STYLE } from "@/lib/format";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarClock, AlertTriangle, ArrowRight, TrendingUp, Trophy, Users as UsersIcon,
  BarChart3, KanbanSquare,
} from "lucide-react";
import { toast } from "sonner";
import { StatTile, FollowupRow, greetingForNow } from "./_shared";

/**
 * ManagerDashboard — Team pulse view.
 * Focus: team follow-up health, leaderboard, pipeline velocity. Action-oriented.
 */
export default function ManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState(null);
  const [board, setBoard] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [home, rep, lb, act] = await Promise.all([
        api.get("/dashboard/home"),
        api.get("/reports/summary").catch(() => ({ data: null })),
        api.get("/admin/team/leaderboard").catch(() => ({ data: null })),
        api.get("/admin/team/activity?limit=6").catch(() => ({ data: { rows: [] } })),
      ]);
      setData(home.data);
      setReports(rep.data);
      setBoard(lb.data);
      setActivity(act.data?.rows || act.data?.activity || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const { indicator: pullIndicator } = usePullToRefresh(load);

  const completeFu = async (id) => {
    try {
      await api.post(`/followups/${id}/complete`);
      toast.success("Follow-up completed");
      load();
    } catch {
      toast.error("Could not complete");
    }
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8 space-y-6">
        <Skeleton className="h-12 w-2/3 rounded-2xl" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
          <Skeleton className="h-28 rounded-3xl" />
        </div>
        <Skeleton className="h-64 rounded-3xl" />
      </div>
    );
  }

  const firstName = (user?.name || user?.email || "").split(/\s+/)[0];
  const boardRows = board?.rows || [];

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-6 sm:space-y-8 max-w-[1180px] mx-auto" data-testid="dashboard-manager">
      {pullIndicator}
      <header className="space-y-1.5 animate-fade-in-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{greetingForNow()} · Manager</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
            {firstName}<span className="text-primary">.</span>
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mt-1">
            Where is the team today — who's ahead, who's stuck, what's overdue.
          </p>
        </div>
      </header>

      {/* Team follow-up health */}
      <section className="grid grid-cols-3 gap-2 sm:gap-4">
        <StatTile label="Team overdue" value={data.overdue_count} hint="Needs attention" accent="text-red-600" icon={AlertTriangle} to="/followups?bucket=overdue" testId="mgr-tile-overdue" />
        <StatTile label="Team today" value={data.today_count} hint="Action required" accent="text-amber-600" icon={CalendarClock} to="/followups?bucket=today" testId="mgr-tile-today" />
        <StatTile label="Team upcoming" value={data.upcoming_count} hint="Plan the week" accent="text-primary-600" icon={TrendingUp} to="/followups?bucket=upcoming" testId="mgr-tile-upcoming" />
      </section>

      {/* Manager quick-actions */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/team" data-testid="mgr-quick-team" className="p-4 rounded-3xl bg-primary text-primary-foreground tap card-lift shadow-soft">
          <Trophy className="h-5 w-5" />
          <p className="font-semibold mt-2 text-sm">Team Pulse</p>
          <p className="text-[11px] opacity-80 mt-0.5">Leaderboard · activity</p>
        </Link>
        <Link to="/pipeline" data-testid="mgr-quick-pipeline" className="p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <KanbanSquare className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Pipeline</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">10-stage board</p>
        </Link>
        <Link to="/reports" data-testid="mgr-quick-reports" className="p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <BarChart3 className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Reports</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Conversion · velocity</p>
        </Link>
        <Link to="/users" data-testid="mgr-quick-users" className="p-4 rounded-3xl bg-card border border-border/60 tap card-lift shadow-soft">
          <UsersIcon className="h-5 w-5 text-primary" />
          <p className="font-semibold mt-2 text-sm">Team roster</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Who's on duty</p>
        </Link>
      </section>

      {/* Leaderboard podium */}
      {boardRows.length > 0 && (
        <section data-testid="mgr-leaderboard">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" />
              <h2 className="font-display text-lg sm:text-xl font-extrabold">Sales Pulse · this month</h2>
            </div>
            <Link to="/team" className="text-xs font-semibold text-primary hover:underline tap inline-flex items-center gap-1">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {boardRows.slice(0, 3).map((r, i) => (
              <Link
                key={r.id}
                to="/team"
                data-testid={`mgr-podium-${i + 1}`}
                className="p-3 sm:p-5 rounded-3xl border-2 bg-gradient-to-br to-card tap card-lift overflow-hidden"
                style={{ borderColor: i === 0 ? "#fcd34d" : i === 1 ? "#d4d4d8" : "#fdba74" }}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Trophy className={`h-3 w-3 ${i === 0 ? "text-yellow-600" : i === 1 ? "text-zinc-500" : "text-orange-600"}`} />
                  <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">#{i + 1}</span>
                </div>
                <p className="text-xs font-bold truncate">{r.name || r.email}</p>
                <p className="font-display text-base sm:text-2xl font-black tracking-tight mt-1 leading-none truncate">{fmtINR(r.won_value)}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{r.won_count} deal{r.won_count !== 1 ? "s" : ""}</p>
              </Link>
            ))}
            {boardRows.length < 3 && Array.from({ length: 3 - boardRows.length }).map((_, i) => (
              <div key={`pe-${i}`} className="p-3 sm:p-5 rounded-3xl border-2 border-dashed border-border/60 text-center text-xs text-muted-foreground grid place-items-center min-h-[88px]">—</div>
            ))}
          </div>
        </section>
      )}

      {/* Team follow-ups needing attention */}
      <section className="grid lg:grid-cols-2 gap-5">
        {[
          { key: "overdue", title: "Team overdue", rows: data.overdue, empty: "Clear. No team overdue." },
          { key: "today", title: "Due today", rows: data.today, empty: "No team items due today." },
        ].map((sec) => {
          const s = BUCKET_STYLE[sec.key];
          return (
            <Card key={sec.key} className="rounded-3xl border-border/60 shadow-soft overflow-hidden" data-testid={`mgr-section-${sec.key}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                    <h3 className="font-display text-lg font-extrabold">{sec.title}</h3>
                  </div>
                  <Badge variant="outline" className={`${s.chip} text-[10px] font-bold uppercase tracking-wider`}>{sec.rows.length}</Badge>
                </div>
                <div className="space-y-2">
                  {sec.rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">{sec.empty}</p>
                  ) : (
                    sec.rows.slice(0, 5).map((fu) => <FollowupRow key={fu.id} fu={fu} onComplete={completeFu} onPing={load} />)
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </section>

      {/* Recent team activity */}
      {activity.length > 0 && (
        <section data-testid="mgr-activity-feed">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg sm:text-xl font-extrabold">Latest team activity</h2>
            <Link to="/team?tab=activity" className="text-xs font-semibold text-primary hover:underline tap inline-flex items-center gap-1">
              Open feed <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-2 divide-y divide-border/60">
              {activity.slice(0, 6).map((a, i) => (
                <div key={a.id || i} className="p-3 flex items-start gap-3">
                  <div className="h-8 w-8 rounded-xl bg-primary-50 text-primary-700 grid place-items-center shrink-0 text-xs font-bold">
                    {(a.user_name || a.owner_name || "—").split(" ").map(x => x[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{a.summary || a.title || a.type || "Activity"}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {a.user_name || a.owner_name || "—"}
                      {a.client_name ? ` · ${a.client_name}` : ""}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Mini-KPI snapshot */}
      {reports && (
        <section className="grid sm:grid-cols-4 gap-4">
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Leads value</p>
              <p className="font-display text-2xl font-black mt-2">{fmtINR(reports.leads_value)}</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">RFQ value</p>
              <p className="font-display text-2xl font-black mt-2">{fmtINR(reports.rfq_value)}</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Won</p>
              <p className="font-display text-2xl font-black mt-2 text-primary">{fmtINR(reports.won_value)}</p>
              <p className="text-xs text-muted-foreground">{reports.won_count} deals</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-border/60 shadow-soft">
            <CardContent className="p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Conversion</p>
              <p className="font-display text-2xl font-black mt-2">{reports.conversion_pct}%</p>
              <p className="text-xs text-muted-foreground">won / total deals</p>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
